import React from "react";
import { Link } from "react-router-dom";
import { policyGroups } from "./policyNavigation";
import "../css/content-page.scss";
import "../css/delivery.scss";

const sidebarGroups = [
  {
    title: "Customer Service",
    links: [
      { label: "Contact Our Team", to: "/contact" },
      { label: "Frequently Asked Questions", to: "/faqs" },
    ],
  },
  {
    title: "Policy Center",
    links: [
      { label: "View All Policies", to: "/policies" },
    ],
  },
  ...policyGroups,
];

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export function CustomerServiceSidebar({ currentPath }) {
  return (
    <aside className="content-page__sidebar" aria-label="Customer service navigation">
      <p className="content-page__sidebar-title">Customer Care</p>
      <nav className="content-page__sidebar-nav">
        {sidebarGroups.map((group) => (
          <section className="content-page__sidebar-group" key={group.title}>
            <h2>{group.title}</h2>
            <ul>
              {group.links.map((link) => {
                const isCurrent = link.to === currentPath;

                return (
                  <li key={link.label}>
                    <Link
                      className={isCurrent ? "is-active" : undefined}
                      aria-current={isCurrent ? "page" : undefined}
                      to={link.to}
                      onClick={scrollToTop}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </aside>
  );
}

function Delivery() {
  return (
    <main className="content-page delivery-page">
      <div className="content-page__shell">
        <CustomerServiceSidebar currentPath="/shipping" />

        <article className="content-page__article" aria-labelledby="delivery-title">
          <header className="content-page__header">
            <p className="content-page__eyebrow">From our shelves to your kitchen</p>
            <h1 id="delivery-title">Shipping &amp; Processing</h1>
            <p className="content-page__lead">
              We pack each sickzuno order with care and keep the journey easy to
              follow from checkout to delivery.
            </p>
          </header>

          <p className="content-page__meta">Last updated: October 16, 2026</p>

          <div className="content-page__notice" role="note">
            <strong>Delivery information:</strong> the final available destination
            and delivery timing are confirmed during checkout.
          </div>

          <section className="content-page__callout" aria-labelledby="shipping-highlight">
            <h2 id="shipping-highlight">Everyday standard shipping</h2>
            <p>
              Standard tracked shipping is charged at $9.99 per order. Stocked items
              are packed in Hong Kong and prepared promptly for carrier collection.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="shipping-options">
            <h2 id="shipping-options">Choose the pace that fits</h2>
            <div className="content-page__grid delivery-page__options">
              <article className="content-page__card">
                <span className="delivery-page__card-kicker">Best value</span>
                <h3>Standard</h3>
                <p>Designed for everyday pantry restocks, with tracking after dispatch.</p>
              </article>
              <article className="content-page__card">
                <span className="delivery-page__card-kicker">When available</span>
                <h3>Expedited</h3>
                <p>Faster services may appear at checkout based on destination and weight.</p>
              </article>
              <article className="content-page__card">
                <span className="delivery-page__card-kicker">Large orders</span>
                <h3>Bulk</h3>
                <p>Orders above five pounds can require added preparation time.</p>
              </article>
            </div>
          </section>

          <section className="content-page__section" aria-labelledby="order-journey">
            <h2 id="order-journey">How your order moves</h2>
            <ol className="content-page__steps">
              <li className="content-page__step">
                <h3>Order received</h3>
                <p>Checkout details are reviewed and available items are reserved.</p>
              </li>
              <li className="content-page__step">
                <h3>Carefully packed</h3>
                <p>Products are gathered, checked and prepared for their trip.</p>
              </li>
              <li className="content-page__step">
                <h3>Tracking ready</h3>
                <p>A shipment update is provided once the carrier accepts the package.</p>
              </li>
            </ol>
          </section>

          <section className="content-page__section" aria-labelledby="delivery-details">
            <h2 id="delivery-details">Destinations &amp; timing</h2>
            <ul className="content-page__list">
              <li>
                Checkout displays only destinations currently supported by sickzuno.
              </li>
              <li>
                Remote destinations may need additional transit time after the carrier
                receives the package.
              </li>
              <li>
                Weather, holidays and carrier interruptions can change an estimated
                arrival date.
              </li>
              <li>
                Orders cannot be placed for destinations excluded from the country list.
              </li>
              <li>
                Customers are responsible for providing a complete deliverable address
                and reviewing it before payment.
              </li>
            </ul>
          </section>

          <section className="content-page__section" aria-labelledby="shipping-changes">
            <h2 id="shipping-changes">Address changes, delays and lost parcels</h2>
            <p>
              Email harv@sickzuno.online immediately if an address is incorrect. An
              address cannot always be changed after packing or carrier collection.
              Carrier estimates are not guaranteed dates. A delayed, missing or damaged
              parcel will be reviewed using the tracking record, delivery details and
              any photographs reasonably needed for the carrier claim.
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="shipping-international">
            <h2 id="shipping-international">International delivery</h2>
            <p>
              Customs duties, import taxes, restricted ingredients and return-to-sender
              charges are covered by the{" "}
              <Link className="content-page__inline-link" to="/international" onClick={scrollToTop}>
                International Orders Policy
              </Link>
              .
            </p>
          </section>

          <section className="content-page__section" aria-labelledby="delivery-tracking">
            <h2 id="delivery-tracking">Tracking and delivery questions</h2>
            <p>
              Carrier scans can take time to update. If a shipment is marked delivered
              but cannot be located, check nearby entrances and household members before
              contacting the carrier or sickzuno customer care.
            </p>
          </section>

          <aside className="content-page__support" aria-label="Shipping support">
            <div>
              <h2>Need help with an order?</h2>
              <p>Email customer care at harv@sickzuno.online.</p>
            </div>
            <Link className="content-page__inline-link" to="/contact" onClick={scrollToTop}>
              Contact customer care
            </Link>
          </aside>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
